import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";

import edit from "../../images/edit.svg";
import Delete from "../../images/delete.svg";
import Logo from "../../images/logo-2.png";
import { useSelector } from "react-redux";
import api from "../../api";
import { Pagination, Stack } from "@mui/material";
import { Loader } from "./loader";
import { Modal } from "react-bootstrap";
import moment from "moment";
import { Email } from "@mui/icons-material";
import "./BlacklistEmail.css";

// Import Lucide React icons
import {
  Search,
  RotateCw,
  X,
  Mail,
  ShieldAlert,
  Calendar,
  Filter,
  Trash2,
  Plus,
  AlertCircle,
  CheckCircle,
  UserX,
} from "lucide-react";
import { useAlert } from "react-alert";

export function BlacklistEmail({ match }) {
  const alert = useAlert();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState([]);
  const [noOfPost, setnoOfPost] = useState(0);
  const [loader, setLoader] = useState(true);
  const [searchMail, setSearchMail] = useState("");
  const [resetPage, setResetPage] = useState(false);
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const getPosts = async () => {
    try {
      let payload = {
        page: page,
      };
      setLoader(true);
      const postData = await api.getBlacklistEmails(payload);
      if (postData?.success) {
        setData(postData.emails);
        setnoOfPost(postData.count);
        setLimit(postData.limit);
      }
      setLoader(false);
    } catch (error) {
      console.log(error);
      setLoader(false);
    }
  };

  const deletePost = async (email) => {
    try {
      setLoader(true);
      const isConfirmed = window.confirm(
        "Are you sure you want to delete this item?",
      );
      if (isConfirmed) {
        const userData = await api.unlockEmail(email);
        console.log("deleted");
        getPosts();
      }
      setLoader(false);
    } catch (error) {
      console.log(error);
      setLoader(false);
    }
  };

  useEffect(() => {
    getPosts();
  }, [page, resetPage]);

  const handleSearch = async () => {
    if (searchMail) {
      let payload = {
        email: searchMail,
      };
      const res = await api.searchBlacklistEmails(payload);
      if (res?.success) {
        setData(res?.emails);
        setLimit(res?.emails?.length || 10);
        setnoOfPost(res?.emails?.length || 0);
      } else {
        alert.error("Invalid Email Address");
      }
    } else {
      alert.info("Please insert email for search");
    }
  };

  const handleReset = () => {
    setResetPage(!resetPage);
    setPage(1);
    setSearchMail("");
  };

  return (
    <>
      {/* Begin Page Content */}
      <div className="container-fluid blacklist-container">
        {/* Content Row */}
        <div className="row">
          {/* Area Chart */}
          <div className="col">
            <div className="blacklist-header">
              <div>
                <h1 className="blacklist-title">
                  <ShieldAlert size={28} />
                  Blacklist Emails
                  <span className="blacklist-title-badge">
                    {noOfPost} Blocked
                  </span>
                </h1>
                <p className="text-muted mt-1">
                  Manage blocked email addresses and their restrictions
                </p>
              </div>

              <div className="blacklist-actions">
                <div className="blacklist-search-container">
                  <div className="blacklist-search-input">
                    <Search size={18} color="#9ca3af" />
                    <input
                      onChange={(e) => setSearchMail(e.target.value)}
                      value={searchMail}
                      type="text"
                      placeholder="Search by email address..."
                      onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                    />
                  </div>
                  <button
                    className="blacklist-search-button"
                    onClick={handleSearch}
                  >
                    <Search size={18} />
                    Search
                  </button>
                  <button
                    className="blacklist-reset-button"
                    onClick={() => handleReset()}
                  >
                    <RotateCw size={18} />
                    Reset
                  </button>
                </div>

                <button className="blacklist-add-button" onClick={handleShow}>
                  <Plus size={18} />
                  Block Email
                </button>
              </div>
            </div>

            {/* Table Component */}
            <Table
              data={data}
              setPage={setPage}
              page={page}
              noOfPost={noOfPost}
              limit={limit}
              deletePost={deletePost}
            />
          </div>
        </div>
      </div>

      {/* Enhanced Modal */}
      <Modal
        show={show}
        onHide={handleClose}
        centered
        className="blacklist-modal"
      >
        <Modal.Body className="p-0">
          <div className="blacklist-modal-content">
            <div className="blacklist-modal-header">
              <div className="blacklist-modal-title">
                <ShieldAlert size={24} />
                <h3>Block New Email</h3>
              </div>
              <button className="blacklist-modal-close" onClick={handleClose}>
                <X size={20} />
              </button>
            </div>
            <AddEmail getPosts={getPosts} setShow={setShow} />
          </div>
        </Modal.Body>
      </Modal>

      {loader && <Loader />}
    </>
  );
}

const Table = ({ data, page, setPage, noOfPost, limit, deletePost }) => {
  const handleChange = (event, value) => {
    setPage(value);
  };

  const formatDate = (date) => {
    let dateTemp = moment(date).format("MMMM Do YYYY, h:mm:ss a");
    return dateTemp;
  };

  return (
    <>
      <div className="blacklist-table-container">
        <div className="blacklist-table-header">
          <h3 className="blacklist-table-title">
            <Mail size={20} />
            Blocked Email Addresses
          </h3>
        </div>

        <div className="table-responsive">
          <table className="blacklist-table">
            <thead>
              <tr>
                <th>
                  <div className="d-flex align-items-center gap-1">
                    <Mail size={16} />
                    Email
                  </div>
                </th>
                <th>
                  <div className="d-flex align-items-center gap-1">
                    <AlertCircle size={16} />
                    Reason
                  </div>
                </th>
                <th>
                  <div className="d-flex align-items-center gap-1">
                    <Calendar size={16} />
                    Created At
                  </div>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((item, key) => (
                  <tr key={key}>
                    <td>
                      <div className="blacklist-table-email">
                        <Mail size={18} color="#6b7280" />
                        <span>{item?.email || "-"}</span>
                      </div>
                    </td>
                    <td>
                      <div
                        className="blacklist-table-reason"
                        title={item?.reason}
                      >
                        {item?.reason || "-"}
                      </div>
                    </td>
                    <td>
                      <div className="blacklist-table-date">
                        <Calendar size={16} color="#9ca3af" />
                        <span>{formatDate(item?.createdAt)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="blacklist-table-actions">
                        <button
                          className="blacklist-table-action-btn blacklist-table-delete-btn"
                          onClick={() => deletePost(item?.email)}
                          title="Unblock email"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4">
                    <div className="blacklist-table-empty">
                      <div className="blacklist-table-empty-icon">
                        <UserX size={48} />
                      </div>
                      <h4 className="blacklist-table-empty-title">
                        No Blocked Emails Found
                      </h4>
                      <p className="blacklist-table-empty-description">
                        No email addresses are currently blocked. Add a new
                        email to the blacklist.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {data.length > 0 && (
          <div className="blacklist-pagination">
            <div className="d-flex justify-content-center">
              <Stack spacing={2}>
                <Pagination
                  count={Math.ceil(noOfPost / limit)}
                  page={page}
                  onChange={handleChange}
                  color="primary"
                  shape="rounded"
                  size="medium"
                />
              </Stack>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export function AddEmail({ getPosts, setShow }) {
  const adminStore = useSelector((state) => state.adminReducer);
  const alert = useAlert();
  const [loader, setLoader] = useState(false);
  const [inputData, setInputData] = useState({
    reason: "",
    email: "",
  });

  const handleSubmit = async () => {
    try {
      if (!inputData.email) {
        alert.info("Please Enter Email");
        return;
      }
      if (!inputData.reason) {
        alert.info("Please Enter Reason");
        return;
      }

      setLoader(true);
      let payload = {
        adminId: adminStore.admin.admin._id,
        email: inputData.email,
        reason: inputData.reason,
      };

      const response = await api.blockEmail(payload);
      if (response?.success) {
        alert.success("Email has been blocked.");
        setInputData({
          email: "",
          reason: "",
        });
        setLoader(false);
        getPosts();
        setShow(false);
      } else {
        setLoader(false);
        alert.error("Something went wrong");
      }
    } catch (error) {
      setLoader(false);
      console.log(error?.message);
      alert.error(error?.message || "Something went wrong");
    }
  };

  return (
    <>
      <div className="blacklist-modal-body">
        <div className="blacklist-form-group">
          <label className="blacklist-form-label">
            <Mail size={18} />
            Email Address
          </label>
          <input
            type="email"
            name="email"
            value={inputData?.email}
            className="blacklist-form-input"
            placeholder="Enter email address to block"
            onChange={(e) =>
              setInputData({ ...inputData, email: e.target.value })
            }
          />
        </div>

        <div className="blacklist-form-group">
          <label className="blacklist-form-label">
            <AlertCircle size={18} />
            Block Reason
          </label>
          <textarea
            name="reason"
            value={inputData?.reason}
            className="blacklist-form-input blacklist-form-textarea"
            onChange={(e) =>
              setInputData({ ...inputData, reason: e.target.value })
            }
            placeholder="Provide a reason for blocking this email address..."
          ></textarea>
        </div>

        <div className="blacklist-form-actions">
          <button
            className="blacklist-form-cancel"
            onClick={() => setShow(false)}
            disabled={loader}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="blacklist-form-submit"
            disabled={loader}
          >
            {loader ? (
              <>
                <div
                  className="spinner-border spinner-border-sm text-light"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                Processing...
              </>
            ) : (
              <>
                <CheckCircle size={18} />
                Block Email
              </>
            )}
          </button>
        </div>
      </div>

      {loader && (
        <div className="blacklist-loader">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
    </>
  );
}
